if (!customElements.get("klaviyo-form")) {
  class KlaviyoForm extends HTMLElement {
    constructor() {
      super();
      this.container = this.querySelector(".klaviyo-form-container");
      this.statusContainer = this.querySelector(".form-status");
      this.form = this.querySelector("form");
      this.listId = this.form.dataset.listId;
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
      this.form.querySelectorAll('input:not([type="checkbox"])').forEach((input) => {
        input.addEventListener("focus", this.handleConsent.bind(this));
      });
      // Close the form on load when the optional "RSVP closes at" time has passed.
      if (this.isClosed()) this.showClosed();
    }

    // Pacific wall-clock minus UTC, in ms, at the instant `ts`.
    static pacificOffset(ts) {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      }).formatToParts(new Date(ts)).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
      const wall = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
      return wall - Math.floor(ts / 1000) * 1000;
    }

    // "YYYY-MM-DD HH:MM" (or "YYYY-MM-DD" = end of that day) in Pacific time -> epoch ms, or null.
    static parsePacific(raw) {
      const m = String(raw).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
      if (!m) return null;
      const hasTime = m[4] !== undefined;
      const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], hasTime ? +m[4] : 23, hasTime ? +m[5] : 59, hasTime ? 0 : 59);
      let ts = wall;
      for (let i = 0; i < 2; i++) ts = wall - KlaviyoForm.pacificOffset(ts);
      return ts;
    }

    isClosed() {
      const raw = (this.form.dataset.closesAt || "").trim();
      if (!raw) return false;
      const closesAt = KlaviyoForm.parsePacific(raw);
      if (closesAt === null) {
        console.warn("klaviyo-form: could not read 'RSVP closes at' value, leaving the form open:", raw);
        return false;
      }
      return Date.now() >= closesAt;
    }

    // Hide the call-to-action heading that sits just above the form (a text block on the Lux page,
    // the .rsvp-callout line in the warehouse hero) so it doesn't sit over "RSVP is closed".
    hideLeadIn() {
      let node = this.parentElement;
      for (let depth = 0; depth < 3 && node; depth++, node = node.parentElement) {
        let prev = node.previousElementSibling;
        while (prev && /^(SCRIPT|STYLE)$/.test(prev.tagName)) prev = prev.previousElementSibling;
        if (prev && prev.matches("h1, h2, h3, h4, .text-block, .rsvp-callout")) {
          prev.style.display = "none";
          return;
        }
      }
    }

    showClosed() {
      this.form.hidden = true;
      this.hideLeadIn();
      this.statusContainer.innerHTML = "";
      const p = document.createElement("p");
      p.className = "success closed";
      p.textContent = this.form.dataset.closedMessage || "RSVP is closed.";
      this.statusContainer.appendChild(p);
    }

    showStatus(type, text) {
      this.statusContainer.innerHTML = "";
      const p = document.createElement("p");
      p.className = type;
      p.textContent = text;
      this.statusContainer.appendChild(p);
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      this.statusContainer.innerHTML = "";
      if (this.isClosed()) {
        this.showClosed();
        return;
      }
      if (!this.validate()) return;
      const emailInput = this.form.querySelector(".email");
      const phoneInput = this.form.querySelector(".phone");
      const payload = {
        data: {
          type: "subscription",
          attributes: {
            custom_source: "form",
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: emailInput ? emailInput.value : "",
                  phone_number: phoneInput ? phoneInput.value : "",
                  properties: {
                    utm_source: localStorage.getItem("utm_source") || "",
                    utm_medium: localStorage.getItem("utm_medium") || "",
                    utm_campaign: localStorage.getItem("utm_campaign") || "",
                  },
                },
              },
            },
          },
          relationships: { list: { data: { type: "list", id: this.listId } } },
        },
      };
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json", revision: "2026-01-15" },
        body: JSON.stringify(payload),
      };
      let res = null;
      try {
        res = await fetch("https://a.klaviyo.com/client/subscriptions/?company_id=PsjVpw", requestOptions);
      } catch (error) {
        console.log("error", error);
      }
      if (res && res.ok) {
        this.showStatus("success", "You're added in the list!");
        this.form.reset();
        setTimeout(() => { this.statusContainer.innerHTML = ""; }, 5000);
        return;
      }
      let message = "Sorry, something went wrong. Please check your details and try again.";
      try {
        const body = res ? await res.json() : null;
        if (body && body.errors && body.errors[0] && body.errors[0].detail) message = body.errors[0].detail;
      } catch (error) { /* keep the generic message */ }
      this.showStatus("error", message);
    }

    validate() {
      let valid = true;
      if (this.form.querySelector(".agreement-block") != null) {
        this.form.querySelectorAll(".agreement-block").forEach((el) => {
          if (el.querySelector('[type="checkbox"]').checked != true) {
            this.showStatus("error", "Please accept the terms.");
            valid = false;
          }
        });
      }
      return valid;
    }

    handleConsent(evt) {
      const field = evt.target.closest(".form-field");
      if (field.querySelector(".agreement-block") != null) {
        field.querySelector(".agreement-block").classList.remove("hidden");
      }
    }
  }
  window.customElements.define("klaviyo-form", KlaviyoForm);
}
